import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Spinner,
  Link,
  SpaceBetween,
  Header,
} from "@amzn/awsui-components-react";
import { getEmployeesByLoginList } from "../../utilities/papiAPIs";

// People data is resolved on the UI from the stored alias — only alias + role
// are persisted. The photo is the badge photo (same source the top nav uses);
// the name and org (business) title are resolved from the People API (PAPI),
// with the alias as a fallback for the name.
const badgePhotoUrl = (alias) =>
  `https://badgephotos.corp.amazon.com/?uid=${encodeURIComponent(alias)}`;
const phonetoolUrl = (alias) =>
  `https://phonetool.amazon.com/users/${encodeURIComponent(alias)}`;

// Contacts with no role fall into this section (rendered last).
const UNGROUPED_LABEL = "Team";

/**
 * Contact Us tab: people to reach out to about RAISE, grouped into sections by
 * their `role` (each distinct role is a section heading). Within a section each
 * person shows their badge photo, name, and org title. The backend stores only
 * alias + role; the name and org title are resolved on the UI from the alias
 * via PAPI (photo via the badge service).
 */
const ContactUs = ({ contacts, roleOrder = [] }) => {
  // alias (lowercased) -> { name, title }, resolved from PAPI.
  const [peopleByAlias, setPeopleByAlias] = useState({});
  // Start in the resolving state when there are aliases to resolve, so the
  // loader shows on the very first render — otherwise the sections would flash
  // raw aliases for one frame before the effect flips this to true.
  const [resolving, setResolving] = useState(
    () => (contacts || []).some((c) => c.alias)
  );

  // All contacts sorted by sort_order (used both for row order within a group
  // and to derive group order from the earliest sort_order in each group).
  const list = useMemo(
    () =>
      [...(contacts || [])].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
      ),
    [contacts]
  );

  // Resolve display name + org title for the contact aliases in one bulk call.
  useEffect(() => {
    const aliases = list.map((c) => c.alias).filter(Boolean);
    if (aliases.length === 0) return;
    let cancelled = false;
    setResolving(true);
    (async () => {
      try {
        const records = await getEmployeesByLoginList(aliases, aliases.length);
        if (cancelled) return;
        const map = {};
        (records || []).forEach((r) => {
          const bi = r.basicInfo || {};
          const login = (bi.login || "").toLowerCase();
          if (!login) return;
          map[login] = {
            name: [bi.firstName, bi.lastName].filter(Boolean).join(" "),
            title: bi.businessTitle || "",
          };
        });
        setPeopleByAlias(map);
      } catch (e) {
        // Non-fatal: fall back to showing the alias as the name, no title.
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [list]);

  const person = (alias) => peopleByAlias[(alias || "").toLowerCase()] || {};
  const displayName = (alias) => person(alias).name || alias;
  const orgTitle = (alias) => person(alias).title || "";

  // Group the (already sort_order-sorted) contacts by role, then order the
  // sections. Section order is driven by the admin-configured `roleOrder`
  // (a list of role labels). Reconciliation is self-healing: only LIVE roles
  // (present in the current contacts) render — a stale/renamed entry in
  // roleOrder is ignored — and any live role NOT in roleOrder is appended by
  // its earliest member sort_order (the previous default behavior).
  const groups = useMemo(() => {
    const byRole = new Map();
    list.forEach((c) => {
      const label = (c.role || "").trim() || UNGROUPED_LABEL;
      if (!byRole.has(label)) {
        byRole.set(label, { label, items: [], order: c.sort_order || 0 });
      }
      byRole.get(label).items.push(c);
    });

    // Rank each live role: its index in roleOrder, else Infinity (unlisted).
    const rank = new Map();
    (roleOrder || []).forEach((label, i) => {
      const key = (label || "").trim();
      if (key && !rank.has(key)) rank.set(key, i);
    });

    return [...byRole.values()].sort((a, b) => {
      const ra = rank.has(a.label) ? rank.get(a.label) : Infinity;
      const rb = rank.has(b.label) ? rank.get(b.label) : Infinity;
      if (ra !== rb) return ra - rb;
      // Both unlisted (or same rank): fall back to earliest member sort_order.
      return a.order - b.order;
    });
  }, [list, roleOrder]);

  // Show a loader until contacts are fetched AND their names are resolved from
  // PAPI, so the sections don't briefly flash raw aliases before the names land.
  if (!contacts || resolving) {
    return (
      <Box padding="l">
        <Spinner /> Loading contacts...
      </Box>
    );
  }

  if (list.length === 0) {
    return <Box variant="p">No contacts have been added yet.</Box>;
  }

  // A single person rendered as a vertical card: photo, name, org title.
  const PersonCard = (c) => (
    <div key={c.contact_id} className="raise-contact-card">
      {/* Same badge-photo source the top nav uses. No onError handler —
          matching the top nav — so a slow/served image isn't hidden. */}
      <img
        className="rounded-circle raise-contact-photo"
        src={badgePhotoUrl(c.alias)}
        alt=""
        width={88}
        height={88}
      />
      <div className="raise-contact-name">
        <Link href={phonetoolUrl(c.alias)} external fontSize="body-m">
          {displayName(c.alias)}
        </Link>
      </div>
      {orgTitle(c.alias) ? (
        <div className="raise-contact-title">{orgTitle(c.alias)}</div>
      ) : null}
    </div>
  );

  return (
    <SpaceBetween size="l">
      <Box variant="p">
        Questions about RAISE? Reach out to the team below.
      </Box>
      {groups.map((group) => (
        <SpaceBetween key={group.label} size="s">
          <Header variant="h3">{group.label}</Header>
          {/* People laid out horizontally, wrapping to new rows as needed. */}
          <div className="raise-contact-row">
            {group.items.map((c) => PersonCard(c))}
          </div>
        </SpaceBetween>
      ))}
    </SpaceBetween>
  );
};

export default ContactUs;
